import { useCallback, useEffect, useState } from "react";
import { api } from "../api";
import Layout from "../components/Layout";
import Swal from "sweetalert2";
import { buildErrorHtml, isBlank, isPositiveInt } from "../utils/validation";

export default function ClassManagement() {

  const [classes, setClasses] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [query, setQuery] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);

  const [form, setForm] = useState({
    name: "",
    course: "",
    instructorId: ""
  });

  const fetchClasses = useCallback(async (pageNumber = 1, q = "") => {
    const params = new URLSearchParams();
    params.set("page", String(pageNumber));
    params.set("limit", "10");
    if (q) {
      params.set("q", q);
    }
    const response = await api.get(`/classes?${params.toString()}`);

    if (Array.isArray(response.data)) {
      setClasses(response.data);
      setPage(1);
      setTotalPages(1);
      return;
    }

    setClasses(response.data?.data || []);
    setPage(response.data?.page || 1);
    setTotalPages(response.data?.totalPages || 1);
  }, []);

  const fetchInstructors = useCallback(async () => {
    const response = await api.get("/instructors");
    const instructorFilter = response.data?.filter((instructor) => instructor.role === "INSTRUCTOR")

    
    setInstructors(instructorFilter);
  }, []);

  useEffect(() => {
    fetchClasses(1, "");
    fetchInstructors();
  }, [fetchClasses, fetchInstructors]);

  function applyFilter() {
    const nextQuery = searchInput.trim();
    setQuery(nextQuery);
    fetchClasses(1, nextQuery);
  }

  function clearFilter() {
    setSearchInput("");
    setQuery("");
    fetchClasses(1, "");
  }

  function openNew() {
    setEditing(null);
    setForm({ name: "", course: "", instructorId: "" });
    setShowModal(true);
  }

  function openEdit(classItem) {
    setEditing(classItem);
    setForm({
      name: classItem.name,
      course: classItem.course,
      instructorId: classItem.instructorId
    });
    setShowModal(true);
  }

  async function save() {
    try {
      const errors = [];

      if (isBlank(form.name)) {
        errors.push("Nome é obrigatório.");
      }

      if (isBlank(form.course)) {
        errors.push("Curso é obrigatório.");
      }

      if (!isPositiveInt(form.instructorId)) {
        errors.push("Selecione um instrutor válido.");
      }

      if (errors.length > 0) {
        Swal.fire({
          icon: "warning",
          title: "Campos inválidos",
          html: buildErrorHtml(errors),
        });
        return;
      }

      if (editing) {
        await api.put(`/classes/${editing.id}`, form);
        Swal.fire("Atualizado!", "Turma atualizada com sucesso.", "success");
      } else {
        await api.post("/classes", form);
        Swal.fire("Criado!", "Turma criada com sucesso.", "success");
      }

      setShowModal(false);
      fetchClasses(page, query);
    } catch (error) {
      Swal.fire("Erro", error.response?.data?.error || "Erro ao salvar.", "error");
    }
  }

  async function remove(id) {
    const confirm = await Swal.fire({
      title: "Excluir turma?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33"
    });

    if (!confirm.isConfirmed) return;

    await api.delete(`/classes/${id}`);
    fetchClasses(page, query);
  }

  return (
    <Layout>

      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4>Gerenciamento de Turmas</h4>
        <button className="btn btn-primary" onClick={openNew}>
          + Nova Turma
        </button>
      </div>

      <div className="card shadow-sm">
        <div className="card-body">
          <div className="row g-2 mb-3">
            <div className="col-md-6">
              <input
                className="form-control"
                placeholder="Buscar por nome da turma ou curso"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") applyFilter();
                }}
              />
            </div>

            <div className="col-md-6 d-flex gap-2">
              <button
                className="btn btn-outline-primary"
                onClick={applyFilter}
              >
                Filtrar
              </button>

              <button
                className="btn btn-outline-secondary"
                onClick={clearFilter}
              >
                Limpar
              </button>
            </div>
          </div>

          <table className="table table-hover align-middle">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Curso</th>
                <th>Instrutor</th>
                <th width="120">Ações</th>
              </tr>
            </thead>
            <tbody>
              {classes.map(c => (
                <tr key={c.id}>
                  <td>{c.name}</td>
                  <td>{c.course}</td>
                  <td>{c.instructor?.name}</td>
                  <td>
                    <button
                      className="btn btn-sm btn-outline-primary me-2"
                      onClick={() => openEdit(c)}
                    >
                      ✏️
                    </button>

                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => remove(c.id)}
                    >
                      🗑
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="d-flex justify-content-center align-items-center mt-3">
            <button
              className="btn btn-outline-secondary me-3"
              disabled={page <= 1}
              onClick={() => fetchClasses(page - 1, query)}
            >
              Anterior
            </button>

            <span>
              Página {page} de {totalPages}
            </span>

            <button
              className="btn btn-outline-secondary ms-3"
              disabled={page >= totalPages}
              onClick={() => fetchClasses(page + 1, query)}
            >
              Próxima
            </button>
          </div>

        </div>
      </div>

      {/* MODAL */}
      {showModal && (
        <div className="custom-modal-overlay">
          <div className="custom-modal">

            <h5>{editing ? "Editar Turma" : "Nova Turma"}</h5>

            <div className="mb-3">
              <label>Nome</label>
              <input
                className="form-control"
                value={form.name}
                onChange={(e) =>
                  setForm({ ...form, name: e.target.value })
                }
              />
            </div>

            <div className="mb-3">
              <label>Curso</label>
              <input
                className="form-control"
                value={form.course}
                onChange={(e) =>
                  setForm({ ...form, course: e.target.value })
                }
              />
            </div>

            <div className="mb-3">
              <label>Instrutor</label>
              <select
                className="form-select"
                value={form.instructorId}
                onChange={(e) =>
                  setForm({ ...form, instructorId: e.target.value })
                }
              >
                <option value="">Selecione</option>
                {instructors.map(i => (
                  <option key={i.id} value={i.id}>
                    {i.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="text-end">
              <button
                className="btn btn-secondary me-2"
                onClick={() => setShowModal(false)}
              >
                Cancelar
              </button>

              <button
                className="btn btn-primary"
                onClick={save}
              >
                Salvar
              </button>
            </div>

          </div>
        </div>
      )}

    </Layout>
  );
}
